'use client'

import { TebutoApi } from '@tebuto/api/public'
import type { LocalitySuggestion } from '@tebuto/api/public/models/LocalitySuggestion'
import type { StreetSuggestion } from '@tebuto/api/public/models/StreetSuggestion'
import { useEffect, useMemo, useState } from 'react'
import useSWR, { type SWRConfiguration } from 'swr'

export type { LocalitySuggestion, StreetSuggestion }

export const SUGGESTION_DEBOUNCE_MS = 1000
export const MIN_QUERY_LENGTH = 2
export const GERMAN_ZIP_REGEX = /^\d{5}$/

const NO_SUGGESTIONS: never[] = []

/** Lookups fail soft (no suggestions), never retry or refetch on focus, and dedupe repeated queries */
const LOOKUP_SWR_CONFIG: SWRConfiguration = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    dedupingInterval: 60_000
}

/**
 * Public API client for address lookups. The embedded widget passes its runtime `apiUrl`
 * (per-host, no credentials); the apps fall back to the standard env base. A dedicated
 * instance instead of the `client-side.ts` singletons keeps the widget bundle free of the
 * credentialed app clients.
 */
function usePublicAddressApi(baseUrl?: string): TebutoApi {
    const resolvedBaseUrl = baseUrl ?? (process.env.NEXT_PUBLIC_PUBLIC_API_URL as string)
    return useMemo(() => new TebutoApi({ BASE: resolvedBaseUrl, WITH_CREDENTIALS: false }), [resolvedBaseUrl])
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value)

    useEffect(() => {
        const timeout = setTimeout(() => setDebounced(value), delayMs)
        return () => clearTimeout(timeout)
    }, [value, delayMs])

    return debounced
}

interface LookupOptions {
    enabled?: boolean
    baseUrl?: string
}

/**
 * Looks up the localities for a complete 5-digit zip code (no debounce — the
 * lookup fires once the zip is complete).
 */
export function useCityLookupByZip(
    zip: string | undefined,
    { enabled = true, baseUrl }: LookupOptions = {}
): { localities: LocalitySuggestion[]; isLoading: boolean } {
    const api = usePublicAddressApi(baseUrl)
    const effectiveZip = enabled && zip && GERMAN_ZIP_REGEX.test(zip) ? zip : undefined

    const { data, isLoading } = useSWR<LocalitySuggestion[]>(
        effectiveZip ? ['addresses/localities', baseUrl ?? '', 'zip', effectiveZip] : null,
        () => api.addresses.searchLocalities(effectiveZip),
        LOOKUP_SWR_CONFIG
    )

    return { localities: (effectiveZip && data) || NO_SUGGESTIONS, isLoading }
}

/**
 * Debounced city name typeahead.
 */
export function useCitySuggestions(
    query: string,
    { enabled = true, baseUrl }: LookupOptions = {}
): { suggestions: LocalitySuggestion[]; isLoading: boolean } {
    const api = usePublicAddressApi(baseUrl)
    const debouncedQuery = useDebouncedValue(query.trim(), SUGGESTION_DEBOUNCE_MS)
    const effectiveQuery = enabled && debouncedQuery.length >= MIN_QUERY_LENGTH ? debouncedQuery : undefined

    const { data, isLoading } = useSWR<LocalitySuggestion[]>(
        effectiveQuery ? ['addresses/localities', baseUrl ?? '', 'name', effectiveQuery] : null,
        () => api.addresses.searchLocalities(undefined, effectiveQuery),
        LOOKUP_SWR_CONFIG
    )

    return { suggestions: (effectiveQuery && data) || NO_SUGGESTIONS, isLoading }
}

/**
 * Debounced street name typeahead, optionally narrowed by zip and/or city.
 */
export function useStreetSuggestions(
    query: string,
    { zip, city }: { zip?: string; city?: string },
    { enabled = true, baseUrl }: LookupOptions = {}
): { suggestions: StreetSuggestion[]; isLoading: boolean } {
    const api = usePublicAddressApi(baseUrl)
    const debouncedQuery = useDebouncedValue(query.trim(), SUGGESTION_DEBOUNCE_MS)
    const effectiveQuery = enabled && debouncedQuery.length >= MIN_QUERY_LENGTH ? debouncedQuery : undefined
    const effectiveZip = zip && GERMAN_ZIP_REGEX.test(zip) ? zip : undefined
    const effectiveCity = city?.trim() || undefined

    const { data, isLoading } = useSWR<StreetSuggestion[]>(
        effectiveQuery
            ? ['addresses/streets', baseUrl ?? '', effectiveQuery, effectiveZip ?? '', effectiveCity ?? '']
            : null,
        () => api.addresses.searchStreets(effectiveQuery as string, effectiveZip, effectiveCity),
        LOOKUP_SWR_CONFIG
    )

    return { suggestions: (effectiveQuery && data) || NO_SUGGESTIONS, isLoading }
}
